import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useConvexAuth, useQuery, useMutation, useAction } from "convex/react";
import { api } from "../convex/_generated/api";
import { hasLogtoAppId, logtoClient } from "./lib/logto-client";
import { claimInitial } from "./lib/identity-claims";
import { Callback } from "./app/callback";
import { OnboardingFlow } from "./onboarding/OnboardingFlow";
import { Skeleton } from "./components/ui/Skeleton";
import { Button } from "./components/ui/Button";
import Chat from "./screens/Chat";
import Today from "./screens/Today";
import ProgressScreen from "./screens/Progress";
import LibraryScreen from "./screens/Library";
import Plan, { DemoPlan } from "./screens/Plan";
import SettingsScreen from "./screens/Settings";
import Guide from "./screens/Guide";
import { Loader2, Salad } from "lucide-react";
import { APP_LAYOUT } from "./lib/app-layout";
import { AnalyticsErrorBoundary } from "./components/AnalyticsErrorBoundary";
import {
  BottomNav,
  HeaderAvatarButton,
  INITIAL_NAV_STATE,
  SCREEN_TEST_ID,
  SCREEN_TITLES,
  navReducer,
} from "./components/nav/BottomNav";
import type { Screen } from "./components/nav/BottomNav";
import { LoggingFlowProvider, useLoggingFlow } from "./lib/logging-flow";
import { PhotoPickerProvider } from "./lib/photo-picker";
import { PlateProvider, usePlate } from "./lib/plate";
import { PlateBar } from "./components/plate/PlateBar";
import { QuickLogLauncher } from "./components/quicklog/QuickLogLauncher";
import { LoggingFlowHost } from "./components/quicklog/LoggingFlowHost";
import { AppTour } from "./components/tour/AppTour";
import { DemoProvider, useDemoMode } from "./lib/demo-mode";
import {
  DemoHistoryCalendar as DemoProgress,
  DemoChat,
  DemoLibrary,
  DemoSettings,
  DemoToday,
} from "./screens/DemoScreens";
import {
  clearDemoModeExitOverride,
  clearDemoModeRequest,
  isDemoModeRequested,
  normalizeInstallUrl,
  persistDemoModeExitOverride,
  readDemoModeExitOverride,
} from "./lib/app-mode";
import { configureDisplayTimezone } from "./lib/format";
import { resolveUserTimezone } from "./lib/timezone";
import { syncSeoMetadata } from "./lib/seo";

function SplashScreen() {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-muted">
      <div className="rounded-full bg-accent-light p-4 text-accent">
        <Salad size={40} strokeWidth={2} />
      </div>
      <div className="flex items-center gap-2 text-text">
        <Loader2 size={20} className="animate-spin" />
        <span className="text-base font-medium">Loading BiteTrack...</span>
      </div>
    </div>
  );
}

function SignInPrompt() {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const isIframed = typeof window !== "undefined" && window.self !== window.top;

  const handleSignIn = async () => {
    setIsSigningIn(true);
    if (isIframed) {
      console.warn("[Frontend][App] Parent auth relay did not respond; retrying iframe handshake");
      window.location.reload();
      return;
    }
    if (!hasLogtoAppId) {
      console.error("[Frontend][App] Standalone sign-in is unavailable because VITE_LOGTO_APP_ID is empty");
      setIsSigningIn(false);
      return;
    }
    console.log("[Frontend][App] Initiating sign-in");
    try {
      // Redirect URI MUST be the stable app origin (e.g.
      // https://<appId>.app.sapps.co/) — this exact string is registered on
      // the SimpleApp Logto SPA client. Passing window.location.href (which
      // may include a path/query) breaks OIDC redirect_uri exact matching.
      await logtoClient.signIn(`${window.location.origin}/`);
    } catch (err) {
      console.error("[Frontend][App] Sign-in failed", err);
      setIsSigningIn(false);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-6 bg-muted px-6">
      <div className="rounded-full bg-accent-light p-5 text-accent">
        <Salad size={48} strokeWidth={2} />
      </div>
      <div className="text-center">
         <h1 className="text-2xl font-bold text-text">BiteTrack</h1>
        <p className="mt-2 max-w-xs text-sm text-text-muted">
          Track calories, log meals, and get <span className="whitespace-nowrap">AI-powered</span> nutrition insights.
        </p>
      </div>
      <Button
        variant="primary"
        size="lg"
        className="w-full max-w-xs"
        onClick={handleSignIn}
        loading={isSigningIn}
      >
        {isIframed ? "Retry connection" : "Sign in"}
      </Button>
    </div>
  );
}

const FALLBACK_HEADER_HEIGHT_PX = 72;

function MainAppShell() {
  const { demoMode, installUrl } = useDemoMode();
  const [nav, dispatchNav] = useReducer(navReducer, INITIAL_NAV_STATE);
  const { launcherOpen, openLauncher, closeLauncher } = useLoggingFlow();
  const { items: plateItems } = usePlate();
  const plateVisible = plateItems.length > 0;
  const currentScreen = nav.screen;
  const profile = useQuery(api.profile.getProfile, demoMode ? "skip" : {});
  const [displayTimezone, setDisplayTimezone] = useState(() => resolveUserTimezone(undefined));
  const [bottomStackHeight, setBottomStackHeight] = useState(0);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(FALLBACK_HEADER_HEIGHT_PX);
  const [plateHeight, setPlateHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const plateRef = useRef<HTMLDivElement>(null);
  const bottomStackRef = useRef<HTMLDivElement>(null);
  const bottomNavRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const scrollPositionsRef = useRef<Partial<Record<Screen, number>>>({});
  const previousScreenRef = useRef<Screen>("chat");

  const profileName = typeof profile?.name === "string" ? profile.name.trim() : "";
  const profileInitial = profileName.length > 0 ? profileName.charAt(0).toUpperCase() : null;
  const [claimsPictureUrl, setClaimsPictureUrl] = useState<string | null>(null);
  const [claimsInitial, setClaimsInitial] = useState<string | null>(null);

  useEffect(() => {
    if (!demoMode && profile === undefined) return;
    const resolvedZone = resolveUserTimezone(demoMode ? undefined : profile?.timezone);
    configureDisplayTimezone(resolvedZone);
    setDisplayTimezone(resolvedZone);
  }, [demoMode, profile]);

  // Avatar identity from Logto ID-token claims. Gracefully no-ops when claims
  // are unavailable: iframe parent-token mode (no local Logto session),
  // signed-out, or demo mode.
  useEffect(() => {
    if (demoMode) return;
    let cancelled = false;
    logtoClient
      .getIdTokenClaims()
      .then((claims) => {
        if (cancelled || claims === null) return;
        const picture =
          typeof claims.picture === "string" && claims.picture.trim().length > 0
            ? claims.picture
            : null;
        const initial = claimInitial(claims.name);
        setClaimsPictureUrl(picture);
        setClaimsInitial(initial);
        console.log("[Frontend][App] Avatar identity claims resolved", {
          hasPicture: picture !== null,
          hasNameInitial: initial !== null,
        });
      })
      .catch((err: unknown) => {
        console.debug("[Frontend][App] Avatar identity claims unavailable", {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [demoMode]);

  const switchScreen = (screen: Screen) => dispatchNav({ type: "select", screen });
  // Central nav button drives the logging-flow provider's launcher state
  // (todo 10); the rotate-45 visual stays synced via quickLogOpen below.
  const handleToggleQuickLog = () => {
    console.log("[Frontend][App] Quick log launcher toggled", { open: !launcherOpen });
    if (launcherOpen) {
      closeLauncher();
    } else {
      openLauncher();
    }
  };

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (main === null) return;
    const previousScreen = previousScreenRef.current;
    scrollPositionsRef.current[previousScreen] = main.scrollTop;
    const nextTop = currentScreen === "chat" ? scrollPositionsRef.current.chat ?? 0 : 0;
    main.scrollTo({ top: nextTop, behavior: "auto" });
    previousScreenRef.current = currentScreen;
  }, [currentScreen]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const nav = bottomNavRef.current;
    if (header === null || nav === null) return;

    const updateChrome = () => {
      setHeaderHeight(header.getBoundingClientRect().height);
      setBottomNavHeight(nav.getBoundingClientRect().height);
    };
    updateChrome();
    const observer = new ResizeObserver(updateChrome);
    observer.observe(header);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const shell = bottomStackRef.current;
    if (shell === null) {
      setBottomStackHeight(0);
      return;
    }

    const updateStack = () => {
      setBottomStackHeight(shell.getBoundingClientRect().height);
    };
    updateStack();
    const observer = new ResizeObserver(updateStack);
    observer.observe(shell);
    return () => observer.disconnect();
  }, [currentScreen]);

  useLayoutEffect(() => {
    const plate = plateRef.current;
    if (plate === null) {
      setPlateHeight(0);
      return;
    }

    const updatePlate = () => {
      setPlateHeight(plate.getBoundingClientRect().height);
    };
    updatePlate();
    const observer = new ResizeObserver(updatePlate);
    observer.observe(plate);
    return () => observer.disconnect();
  }, [plateVisible]);

  const title = SCREEN_TITLES[currentScreen];
  const bottomChromeHeight =
    currentScreen === "chat" && bottomStackHeight > 0 ? bottomStackHeight : bottomNavHeight;
  const contentPaddingBottom = bottomChromeHeight + (plateVisible ? plateHeight : 0) + 16;

  return (
     <div
       key={displayTimezone}
       className="flex h-dvh min-h-0 w-full flex-col overflow-hidden bg-muted"
       data-testid="app-shell"
       style={{ "--app-header-h": `${headerHeight}px` } as CSSProperties}
     >
      <header ref={headerRef} className="fixed top-0 z-20 w-full border-b border-border bg-surface/95 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="mx-auto max-w-xl px-4 py-3">
           <div className="flex items-center justify-between gap-3">
             <h1 className="text-lg font-semibold text-text">{demoMode ? "BiteTrack Demo" : title}</h1>
             <div className="flex items-center gap-2">
               {demoMode && installUrl ? (
                 <a
                   data-testid="demo-install-btn"
                   href={installUrl}
                   target="_blank"
                   rel="noreferrer"
                   className="inline-flex min-h-[44px] items-center rounded-xl bg-accent px-3 py-2 text-sm font-semibold text-white active:scale-[0.98] select-none"
                 >
                   Install
                 </a>
               ) : demoMode ? (
                 <span data-testid="demo-install-unavailable" className="rounded-xl border border-border bg-muted px-3 py-2 text-xs font-semibold text-text-muted">
                   Install link unavailable
                 </span>
               ) : null}
                <HeaderAvatarButton
                  initial={claimsInitial ?? profileInitial}
                  pictureUrl={claimsPictureUrl}
                  onOpenSettings={() => dispatchNav({ type: "openSettings" })}
                />
             </div>
           </div>
        </div>
      </header>

        <main ref={mainRef} className={APP_LAYOUT.mainScrollClass}>
        <div
          className={APP_LAYOUT.contentClass}
          style={{ paddingTop: headerHeight + 12, paddingBottom: contentPaddingBottom }}
          data-testid={SCREEN_TEST_ID[currentScreen]}
        >
           {currentScreen === "chat" && (demoMode ? <DemoChat /> : <Chat onSwitchScreen={(s) => switchScreen(s as Screen)} />)}
           {currentScreen === "today" && (demoMode ? <DemoToday /> : <Today onSwitchScreen={(s) => switchScreen(s as Screen)} />)}
           {currentScreen === "progress" && (
             <AnalyticsErrorBoundary>
                {demoMode ? <DemoProgress /> : <ProgressScreen />}
             </AnalyticsErrorBoundary>
           )}
           {currentScreen === "plan" && (demoMode ? <DemoPlan /> : <Plan />)}
           {currentScreen === "library" && (demoMode ? <DemoLibrary /> : <LibraryScreen onSwitchScreen={(s) => switchScreen(s as Screen)} />)}
            {currentScreen === "settings" && (demoMode ? <DemoSettings onSwitchScreen={(s) => switchScreen(s as Screen)} /> : <SettingsScreen onSwitchScreen={(s) => switchScreen(s as Screen)} />)}
            {currentScreen === "guide" && <Guide onSwitchScreen={(s) => switchScreen(s as Screen)} />}
        </div>
      </main>

      {currentScreen === "chat" && (
        <div
          ref={bottomStackRef}
          className="fixed inset-x-0 bottom-0 z-20 flex flex-col shadow-[0_-8px_24px_rgba(38,51,44,0.08)]"
          data-testid="chat-composer-shell"
        >
          <div data-testid="chat-composer-portal" />
          <div className="pointer-events-none" aria-hidden="true" style={{ height: bottomNavHeight }} />
        </div>
      )}

      <BottomNav
        currentScreen={currentScreen}
        quickLogOpen={launcherOpen}
        onSelectScreen={switchScreen}
        onToggleQuickLog={handleToggleQuickLog}
        navRef={bottomNavRef}
      />

      {/* Temporary Plate bar (todo 11): fixed above the bottom nav — or above
          the chat composer stack on the chat screen so it never covers the
          composer. The wrapper owns the fixed positioning so MainAppShell can
          measure it and pad the scroll content; PlateBar renders the card. */}
      {plateVisible && (
        <div
          ref={plateRef}
          className="pointer-events-none fixed inset-x-0 z-30"
          style={{ bottom: bottomChromeHeight }}
        >
          <PlateBar demoMode={demoMode} />
        </div>
      )}

      {/* Logging-flow overlay layer (todo 10): launcher + flow host sheets
          render above all content (z-40), below nothing. */}
      <QuickLogLauncher />
      <LoggingFlowHost demoMode={demoMode} onNavigate={switchScreen} />

      <AppTour
        enabled={!demoMode && profile !== undefined && profile !== null && profile.onboardingComplete === true}
        currentScreen={currentScreen}
        onNavigate={switchScreen}
      />
    </div>
  );
}

function MainApp() {
  return (
    <LoggingFlowProvider>
      <PhotoPickerProvider>
        <PlateProvider>
          <MainAppShell />
        </PlateProvider>
      </PhotoPickerProvider>
    </LoggingFlowProvider>
  );
}

function ProfileLoader() {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-muted px-6">
      <Skeleton className="h-16 w-16 rounded-full" />
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-48" />
    </div>
  );
}

function AuthenticatedApp() {
  const profile = useQuery(api.profile.getProfile, {});
  const recalculate = useMutation(api.engine.recalculate);
  const recalcFiredRef = useRef(false);

  useEffect(() => {
    console.log("[Frontend][App] Profile loaded", {
      onboardingComplete: profile?.onboardingComplete ?? null,
      dailyGoalKcal: profile?.dailyGoalKcal ?? null,
    });
  }, [profile]);

  useEffect(() => {
    if (profile === undefined || profile === null || !profile.onboardingComplete) return;
    if (recalcFiredRef.current) return;
    recalcFiredRef.current = true;
    console.log("[Frontend][Engine] Recalculate-on-open fired");
    recalculate({ clientNow: Date.now() })
      .then(() => {
        console.log("[Frontend][Engine] Recalculate-on-open completed");
      })
      .catch((err: unknown) => {
        console.warn("[Frontend][Engine] Recalculate-on-open failed", {
          error: err instanceof Error ? err.message : String(err),
        });
      });
  }, [profile, recalculate]);

  if (profile === undefined) {
    return <ProfileLoader />;
  }

  if (profile === null || !profile.onboardingComplete) {
    return <OnboardingFlow />;
  }

  return <MainApp />;
}

function DemoApp() {
  return <MainApp />;
}

function DemoExitGate({ onVerified, onCancel }: { onVerified: () => void; onCancel: () => void }) {
  const verify = useAction(api.demo.verify);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const candidate = password.trim();
    if (!candidate) {
      setError("Enter the demo password to continue.");
      return;
    }
    setBusy(true);
    setError(null);
    console.log("[Frontend][DemoGate] Verifying password before exiting demo");
    try {
      const result = await verify({ password: candidate });
      if (!result.enabled) {
        setError("That password did not authorize leaving demo mode.");
        console.warn("[Frontend][DemoGate] Exit password rejected");
        return;
      }
      onVerified();
    } catch (err: unknown) {
      console.error("[Frontend][DemoGate] Verification failed", { error: err instanceof Error ? err.message : String(err) });
      setError("Demo mode could not be verified. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-muted px-5" data-testid="demo-password-gate">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-5 shadow-sm">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">BiteTrack demo</p>
          <h1 className="mt-1 text-xl font-semibold text-text">Exit demo mode</h1>
          <p className="mt-2 text-sm leading-relaxed text-text-muted">Confirm the owner password to return to the signed-in app.</p>
        </div>
        <input
          data-testid="demo-password-input"
          type="password"
          value={password}
          onChange={(event) => { setPassword(event.target.value); setError(null); }}
          onKeyDown={(event) => { if (event.key === "Enter") void handleSubmit(); }}
          placeholder="Owner password"
          autoComplete="current-password"
          className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-base text-text focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {error && <p className="mt-2 text-sm text-danger" role="alert">{error}</p>}
        <Button data-testid="demo-password-submit" className="mt-4 w-full" loading={busy} onClick={() => void handleSubmit()}>Exit demo</Button>
        <Button data-testid="demo-password-cancel" className="mt-2 w-full" variant="ghost" onClick={onCancel}>Stay in demo</Button>
      </div>
    </div>
  );
}

function AppContent() {
  const { demoMode } = useDemoMode();
  if (demoMode) return <DemoApp />;
  return <AppWithAuth />;
}

function AppWithAuth() {
  const { isLoading, isAuthenticated } = useConvexAuth();

  useEffect(() => {
    console.log("[Frontend][App] Auth state", { isLoading, isAuthenticated });
  }, [isLoading, isAuthenticated]);

  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;
  const isAuthCallback =
    !!searchParams?.has("code") && !!searchParams?.has("state");

  if (isAuthCallback) return <Callback />;
  if (isLoading) return <SplashScreen />;
  if (!isAuthenticated) return <SignInPrompt />;
  return <AuthenticatedApp />;
}

export default function App() {
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  const storeUser = useMutation(api.auth.storeUser);
  const recordDemoExit = useMutation(api.demo.recordExit).withOptimisticUpdate(
    (localStore) => {
      const current = localStore.getQuery(api.demo.getExitState, {});
      if (current !== undefined) {
        localStore.setQuery(api.demo.getExitState, {}, { exited: true });
      }
    },
  );
  const clearDemoExit = useMutation(api.demo.clearExit).withOptimisticUpdate(
    (localStore) => {
      const current = localStore.getQuery(api.demo.getExitState, {});
      if (current !== undefined) {
        localStore.setQuery(api.demo.getExitState, {}, { exited: false });
      }
    },
  );
  const localDemoRequested = isDemoModeRequested();
  const localDemoExitOverride = readDemoModeExitOverride();
  const canResolvePublicDemo = typeof window !== "undefined" && window.location.protocol === "https:";
  const publicDemoConfig = useQuery(api.demo.getPublicConfig, canResolvePublicDemo ? {} : "skip");
  const [authUserReady, setAuthUserReady] = useState(false);
  const serverDemoExit = useQuery(
    api.demo.getExitState,
    isAuthenticated && authUserReady ? {} : "skip",
  );
  const [demoMode, setDemoMode] = useState(localDemoRequested);
  const [demoVerified, setDemoVerified] = useState(localDemoRequested);
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [showExitGate, setShowExitGate] = useState(false);
  const [modeResolved, setModeResolved] = useState(!canResolvePublicDemo);
  const [demoExitConfirmed, setDemoExitConfirmed] = useState(false);
  const serverExitReady = !isAuthenticated || localDemoRequested || localDemoExitOverride || (
    authUserReady && serverDemoExit !== undefined
  );
  const effectiveExitOverride = localDemoExitOverride || serverDemoExit?.exited === true;
  const exitStateReady = publicDemoConfig?.enabled === false || serverExitReady;
  const resolvedModeReady = modeResolved && (!canResolvePublicDemo || (!authLoading && exitStateReady));

  const seoDemoActive = demoMode && demoVerified;
  useEffect(() => {
    syncSeoMetadata(seoDemoActive ? "demo" : "default");
  }, [seoDemoActive]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      setAuthUserReady(false);
      return;
    }
    let active = true;
    console.log("[Frontend][App] Storing user after sign-in");
    storeUser({})
      .then(() => {
        if (active) setAuthUserReady(true);
      })
      .catch((err: unknown) => {
        console.error("[Frontend][App] storeUser failed", {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      active = false;
    };
  }, [authLoading, isAuthenticated, storeUser]);

  useEffect(() => {
    if (!localDemoRequested) return;
    clearDemoModeExitOverride();
    if (!isAuthenticated || !authUserReady) return;
    clearDemoExit({}).catch((err: unknown) => {
      console.warn("[Frontend][App] Explicit demo entry could not clear the server exit override", {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }, [authUserReady, clearDemoExit, isAuthenticated, localDemoRequested]);

  useEffect(() => {
    if (
      demoExitConfirmed ||
      publicDemoConfig === undefined ||
      authLoading ||
      !exitStateReady
    ) return;
    const enabled = publicDemoConfig.enabled;
    setInstallUrl(enabled ? normalizeInstallUrl(publicDemoConfig.installUrl) : null);
    const shouldEnableDemo = enabled && (localDemoRequested || !effectiveExitOverride);
    setDemoMode(shouldEnableDemo);
    setDemoVerified(shouldEnableDemo);
    setModeResolved(true);
    console.log("[Frontend][App] Public demo availability resolved", {
      enabled,
      exitOverride: effectiveExitOverride,
      explicitEntry: localDemoRequested,
      hasInstallUrl: enabled && publicDemoConfig.installUrl.trim().length > 0,
    });
  }, [
    authLoading,
    demoExitConfirmed,
    effectiveExitOverride,
    localDemoRequested,
    publicDemoConfig,
    exitStateReady,
  ]);

  const requestDisableDemo = () => {
    console.log("[Frontend][App] Exit demo requested; waiting for password verification");
    setShowExitGate(true);
  };

  const disableDemo = () => {
    clearDemoModeRequest();
    persistDemoModeExitOverride();
    if (isAuthenticated) {
      const recordExit = authUserReady
        ? recordDemoExit({})
        : storeUser({}).then(() => recordDemoExit({}));
      recordExit.catch((err: unknown) => {
        console.warn("[Frontend][App] Durable server demo exit could not be recorded", {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    }
    console.log("[Frontend][App] Demo exit verified; durable exit override set");
    setDemoExitConfirmed(true);
    setShowExitGate(false);
    setDemoVerified(false);
    setInstallUrl(null);
    setDemoMode(false);
  };

  return (
    <DemoProvider enabled={demoMode && demoVerified} installUrl={installUrl} onDisable={requestDisableDemo} onEnable={() => {
      clearDemoModeExitOverride();
      if (isAuthenticated) {
        const clearExit = authUserReady
          ? clearDemoExit({})
          : storeUser({}).then(() => clearDemoExit({}));
        clearExit.catch((err: unknown) => {
          console.warn("[Frontend][App] Demo re-entry could not clear the server exit override", {
            error: err instanceof Error ? err.message : String(err),
          });
        });
      }
      console.log("[Frontend][App] Demo enabled from authenticated Settings");
      setDemoExitConfirmed(false);
      setDemoVerified(true);
      setDemoMode(true);
    }}>
      {!resolvedModeReady ? <SplashScreen /> : showExitGate && demoMode ? <DemoExitGate onVerified={disableDemo} onCancel={() => setShowExitGate(false)} /> : <AppContent />}
    </DemoProvider>
  );
}
